import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useWallets } from '../../../../src/Hooks/wallet/use-wallets';

const purchaseSchema = z.object({
    amount: z.coerce.number().positive('Amount must be a positive number'),
    currency_code: z.string().length(3, 'Currency code must be 3 characters'),
    reference_id: z.string().optional().or(z.literal('')),
    reference_type: z.string().optional().or(z.literal('')),
    description: z.string().optional().or(z.literal('')),
});

const PurchasePage = () => {
    const { sidebar } = useContext(AppContext);
    const { loading, fetchCurrencies } = useWallets();
    const [currencies, setCurrencies] = useState([]);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({ resolver: zodResolver(purchaseSchema) });

    useEffect(() => {
        const loadCurrencies = async () => {
            try {
                const res = await fetchCurrencies();
                setCurrencies(res?.data || []);
            } catch {
                // handled
            }
        };
        loadCurrencies();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onSubmit = async (data) => {
        try {
            // Purchase uses the debit flow
            const { default: apiClient } = await import('../../api/wallet/client');
            const res = await apiClient.post('/debit', data);
            if (res.data?.success) {
                const { default: toast } = await import('react-hot-toast');
                toast.success('Purchase successful!');
                reset();
            }
        } catch {
            // handled by interceptor
        }
    };

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">Purchase</h6>
            </div>
                <div className="fade-in">
            
            <Card className="border-0 shadow-sm" style={{ maxWidth: 600 }}>
                <Card.Body className="p-4">
                    <Alert variant="info" className="mb-3">
                        <strong>Note:</strong> Reward balance is used first, then real-money.
                    </Alert>

                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <Form.Group className="mb-3">
                            <Form.Label>Amount</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                placeholder="Enter amount"
                                isInvalid={!!errors.amount}
                                {...register('amount')}
                            />
                            <Form.Control.Feedback type="invalid">{errors.amount?.message}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Currency</Form.Label>
                            <Form.Select isInvalid={!!errors.currency_code} {...register('currency_code')}>
                                <option value="">Select currency</option>
                                {currencies.map((c) => (
                                    <option key={c.code} value={c.code}>
                                        {c.symbol} {c.code}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{errors.currency_code?.message}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Reference ID <span className="text-muted">(optional)</span></Form.Label>
                            <Form.Control type="text" placeholder="e.g., ORDER-001" {...register('reference_id')} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Reference Type <span className="text-muted">(optional)</span></Form.Label>
                            <Form.Control type="text" placeholder="e.g., order" {...register('reference_type')} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description <span className="text-muted">(optional)</span></Form.Label>
                            <Form.Control type="text" placeholder="e.g., Cafeteria lunch" {...register('description')} />
                        </Form.Group>

                        <Button type="submit" variant="warning" disabled={loading} className="w-100 text-white">
                            {loading ? <Spinner size="sm" /> : 'Make Purchase'}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </div>
        </div>
        </div>
    );
};

export default PurchasePage;
